import { z } from 'zod';
import { CrmRole } from './crmRole';

/**
 * Split de pagamento da venda (#748) — quem recebe o quê de cada cobrança.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas: contrato do split de
 * pagamento de parceiro e comissões).
 *
 * Decisões do owner (2026-09-26/27), que este módulo implementa e os testes
 * fixam:
 *
 * - **Parceiro** (modelo DIRECT): o percentual do cadastro dele (0–50), sobre o
 *   **líquido** da cobrança (depois da taxa do gateway).
 * - **EtiquetaChef**: fica com o restante — e a parte do EtiquetaChef é a BASE
 *   das comissões internas. Exemplo de referência do owner, venda 100: parceiro
 *   50, EtiquetaChef 50, vendedor 10 % da parte do EtiquetaChef = 5, gerente
 *   5 % = 2,5.
 * - **Internos**: venda sem parceiro fechada por SELLER → vendedor + gerente do
 *   lead; fechada por MANAGER → só ele (com o percentual de vendedor); venda de
 *   parceiro → o gerente do parceiro. Participação não existe (#758).
 * - **Duração**: por beneficiário (`window_months`, configurado no usuário,
 *   congelado na venda). O parceiro recebe enquanto a assinatura existir.
 * - **Sem destino** (carteira Asaas / conta Connect): o beneficiário continua
 *   na venda com `transfer_mode: MANUAL` — a comissão dele vai para o extrato
 *   a pagar. Nunca bloqueia a venda.
 *
 * O gateway só aceita percentual sobre o líquido INTEIRO, então cada
 * percentual interno é convertido: `net_percent = percent × (100 − c) / 100`,
 * com `c` = percentual do parceiro (0 sem parceiro). O Asaas aceita 4 casas.
 *
 * Tudo aqui é puro: o snapshot é montado uma vez na venda e congelado em
 * `/salesSplits/{tenantId}` (princípio 3 — preço e comissão congelados).
 *
 * **Por que doc próprio, e não um campo de `billing/subscription`:** a
 * assinatura é lida por qualquer membro do tenant cliente (`firestore.rules`,
 * `match /billing/{document=**}`). No campo, o restaurante leria o percentual
 * do parceiro, os uids e percentuais de vendedor e gerente e as carteiras —
 * a mesma classe do achado #25 da auditoria da #679 (`remit_cents` legível
 * pelo tenant). `/salesSplits` é server-only; leitura só super-admin.
 *
 * Usado por: Functions — `lib/billing/saleSplitSnapshot.ts` (`freezeSaleSplit`,
 * a cada link da venda), `billing/asaas/payOption.ts` (regras de split da
 * escolha na `/pay`), `billing/webhooks/asaas/asaasHandlers.ts` (abre a janela
 * no 1º pagamento); CRM — cadastro do parceiro (`features/partners/partnerForm.ts`)
 * e do colaborador (`features/sellers/SellerConfigPage.tsx`); tipos —
 * `crm.ts`, `crmPartner.ts`, `assistedSale.ts`. Android e iOS não leem
 * (`/salesSplits` é server-only e não está no contrato gerado).
 *
 * Arquitetura: lógica pura em `@etiquetachef/types` para que CRM (validação de
 * formulário) e Functions (execução) usem a MESMA regra; só Functions
 * escreve o doc. Nenhuma função aqui lê relógio: o `now` vem do servidor.
 *
 * Dados — `/salesSplits/{tenantId}` (schema `SalesSplitDocSchema`), exemplo
 * anonimizado de venda de parceiro DIRECT a 20 % pelo Asaas:
 *   {
 *     "tenant_id": "tenant-1",
 *     "split": {
 *       "gateway": "asaas",
 *       "partner_id": "partner-1",
 *       "beneficiaries": [
 *         { "role": "PARTNER", "user_id": null, "percent": 20, "net_percent": 20,
 *           "destination": "00000000-0000-4000-8000-000000000000",
 *           "transfer_mode": "ASAAS_SPLIT", "window_months": null },
 *         { "role": "PARTNER_MANAGER", "user_id": "uid-1", "percent": 10, "net_percent": 8,
 *           "destination": null, "transfer_mode": "MANUAL", "window_months": 12 }
 *       ],
 *       "window_start_at": null,        // epoch ms; nulo até o 1º pagamento
 *       "frozen_at": 1790000000000      // epoch ms do servidor
 *     },
 *     "updated_at": 1790000000000
 *   }
 * Armadilhas: `percent` é a base do CADASTRO (parceiro: % do líquido; internos:
 * % da parte do EtiquetaChef), `net_percent` é o que vai ao gateway; ms, não s.
 *
 * Falhas conhecidas:
 *  - 2026-09-27 12:34 +0200 (#748, auditoria da A1/A4): com o parceiro a 0 %
 *    o split perdia o `partner_id`; soma sem tolerância de ponto flutuante;
 *    o mesmo colaborador podia entrar duas vezes. Corrigidos no `superRefine`
 *    e em `buildSalesSplit` (ver comentários locais).
 *  - 2026-09-27 12:23 +0200 (#753): "no máximo 2 casas" recusava 10,12 por
 *    comparação exata de ponto flutuante (`hasAtMostTwoDecimals`).
 *  - 2026-09-27 15:48 +0200 (#752, A3, B-M7): o split era congelado ANTES de o
 *    link dar certo, e um link do Stripe que falhava reescrevia o split do link
 *    do Asaas vivo. A correção mora em quem chama (`freezeSaleSplit` depois do
 *    link) e na `/pay`, que recusa split ausente ou de outro gateway.
 *
 * Dicas: `npm test -w @etiquetachef/types -- salesSplit` fixa o exemplo do
 * owner (100 → 50 / 5 / 2,5). O Asaas mede o split sobre o líquido
 * (docs/billing/asaas.md, pergunta 2: R$ 199,90 no cartão → líquido 193,44 →
 * 20 % = 38,68). O `PUT` de split não retroage (pergunta 3).
 *
 * Issues: #748 (épico Asaas), #750 (A1: contrato), #752 (A3: venda e `/pay`),
 * #753 (A4: cadastro e extrato a pagar), #757 (A8: Stripe Connect do parceiro),
 * #758 (fim da comissão de participação), #740 (janela de comissão), #679
 * (achado #25: dado de comissão legível pelo tenant).
 *
 * Histórico:
 *  - 2026-09-27 12:00 +0200 — (#750) módulo criado: snapshot, janela, regras do Asaas.
 *  - 2026-09-27 12:23 +0200 — (#753) `hasAtMostTwoDecimals` com tolerância.
 *  - 2026-09-27 12:34 +0200 — (#748) auditoria A1/A4: `asaasDestinationOrNull`,
 *    `partner_id` a 0 %, soma com tolerância, colaborador duplicado.
 *  - 2026-09-27 14:28 +0200 — (#752) `saleSplitInternals`, `SplitCollaborator`,
 *    `partnerSplitDestination` para a venda da A3.
 */

/**
 * Gateway que executa o split.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Por quê: congelado no snapshot junto com o resto — o `transfer_mode` de
 * cada beneficiário depende dele (`transferModeFor`), e a escolha na `/pay`
 * recusa split de OUTRO gateway que não o da venda (#752 — um split congelado
 * para o Stripe não pode virar regra de carteira Asaas). Minúsculo porque é o
 * mesmo vocabulário de `external_provider` do `billing/subscription`.
 *
 * Usado por: `SalesSplitSchema`; `CrmPartnerSchema.paymentGateway` e
 * `CrmUpsertPartnerInput` (`crmPartner.ts`); `GetSaleCheckoutOptionsOutput`
 * (`assistedSale.ts`); o tipo `SplitGateway` em `lib/billing/saleGateway.ts`,
 * `createSaleCheckoutLink.ts`, `saleSplitSnapshot.ts` (Functions) e
 * `features/partners/partnerForm.ts` (CRM).
 *
 * Issues: #748 (épico), #750 (A1), #752 (A3), #757 (Connect).
 */
export const SplitGatewaySchema = z.enum(['asaas', 'stripe']);
export type SplitGateway = z.infer<typeof SplitGatewaySchema>;

/**
 * Papel do beneficiário NA VENDA (não o papel do usuário no CRM).
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * - `PARTNER` — o parceiro DIRECT, identificado por `partner_id` (sem uid);
 * - `PARTNER_MANAGER` — o gerente interno responsável pelo parceiro, com o
 *   percentual do cadastro do PARCEIRO;
 * - `SELLER` — quem fechou a venda sem parceiro (também o MANAGER que fecha
 *   sozinho: ele entra como `SELLER`, com o percentual de vendedor dele);
 * - `MANAGER` — o gerente do lead, quando quem fechou não foi ele.
 *
 * Regra completa em `saleSplitInternals`.
 *
 * Usado por: `SplitBeneficiarySchema.role` e `BuildSalesSplitArgs` (só este
 * módulo; nenhum consumidor externo importa o enum).
 *
 * Issues: #748, #750, #758 (participação fora do split).
 */
export const SplitRoleSchema = z.enum(['PARTNER', 'PARTNER_MANAGER', 'SELLER', 'MANAGER']);
export type SplitRole = z.infer<typeof SplitRoleSchema>;
/**
 * Os papéis de beneficiário do split como constantes (`SplitRole.PARTNER`).
 *
 * Origem: #761 — 2026-09-27 17:09 +0200 (o enum é da A1, #750, `7be9cd24b`).
 *
 * Por quê: `SplitRole.SELLER`/`MANAGER` NÃO são `CrmRole.SELLER`/`MANAGER` — o
 * gerente do split é o gerente do LEAD, e um `CrmRole.MANAGER` pode entrar no
 * split como `PARTNER_MANAGER`. Nome de constante por conceito evita a troca.
 *
 * Contrato: `SplitRoleSchema.enum`; valores gravados iguais.
 *
 * Usado por: este módulo (`buildSalesSplit`, `saleSplitInternals`, o refine
 * de `SalesSplitSchema`).
 *
 * Issues: #761, #750.
 */
export const SplitRole = SplitRoleSchema.enum;

/**
 * Como a parte chega ao beneficiário. `ASAAS_SPLIT`/`STRIPE_CONNECT`: o gateway
 * repassa sozinho em cada cobrança. `MANUAL`: vai para o extrato a pagar.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Por quê: "sem carteira" não pode bloquear a venda (owner, #748); o modo
 * `MANUAL` mantém o beneficiário no snapshot para o extrato a pagar (A4, #753)
 * calcular a comissão dele fora do gateway.
 *
 * Usado por: `SplitBeneficiarySchema.transfer_mode`, `transferModeFor`,
 * `asaasSplitRules` e `manualSplitBeneficiaries` (só este módulo).
 *
 * Issues: #748, #750, #753 (extrato a pagar), #757 (`STRIPE_CONNECT`, A8).
 */
export const SplitTransferModeSchema = z.enum(['ASAAS_SPLIT', 'STRIPE_CONNECT', 'MANUAL']);
export type SplitTransferMode = z.infer<typeof SplitTransferModeSchema>;

/**
 * Teto do percentual do parceiro sobre o líquido.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (decisão do owner de 2026-09-26, #748).
 *
 * Por quê: acima de 50 o EtiquetaChef ficaria com menos que o parceiro, e a
 * base das comissões internas (a parte do EtiquetaChef) encolheria abaixo do
 * que o cadastro dos colaboradores pressupõe.
 *
 * Usado por: `SplitPartnerPercentSchema` (escrita do cadastro do parceiro).
 * A LEITURA do cadastro (`CrmPartnerSchema.commissionPercent`) aceita até 100.
 */
export const SPLIT_PARTNER_PERCENT_MAX = 50;
/**
 * Janela de comissão padrão de um colaborador interno: um ano a partir do 1º
 * pagamento.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Por quê: 12 é a regra da #740 que valia para todos antes de a janela virar
 * configuração por usuário (#748); é também o valor que o cadastro sem o campo
 * (ou ilegível) assume (`windowOf`, `CrmUserSchema.commissionWindowMonths`).
 *
 * Usado por: `windowOf` (este módulo); `CrmUserSchema` (`crm.ts`, `.catch`);
 * CRM `features/sellers/SellerConfigPage.tsx` (valor inicial do campo).
 */
export const COMMISSION_WINDOW_MONTHS_DEFAULT = 12;
/**
 * Menor janela aceita: janela 0 seria "não recebe", que se expressa com
 * percentual 0.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200.
 *
 * Usado por: `CommissionWindowMonthsSchema`.
 */
export const COMMISSION_WINDOW_MONTHS_MIN = 1;
/**
 * Maior janela aceita (10 anos) — barra digitação absurda no cadastro do
 * colaborador.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200.
 *
 * Usado por: `CommissionWindowMonthsSchema`.
 */
export const COMMISSION_WINDOW_MONTHS_MAX = 120;
/**
 * Casas decimais que o `percentualValue` do Asaas aceita.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (medido no spike A0, #749:
 * docs/billing/asaas.md, "Consequências para as fases", A1).
 *
 * Usado por: `roundPercent` (este módulo).
 */
export const SPLIT_PERCENT_DECIMALS = 4;

/**
 * Duração, em meses de calendário, da comissão de um colaborador interno,
 * contada do 1º pagamento (`window_start_at`).
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Por quê: inteiro porque a conta é feita por `addCalendarMonthsUtc`, que não
 * tem mês fracionário; 1–120 pelos limites acima.
 *
 * Usado por: `SplitBeneficiarySchema.window_months`, `windowOf` (este módulo);
 * `CrmUserSchema.commissionWindowMonths` (leitura tolerante, `.catch`) e
 * `CrmUpdateUserConfigInputSchema` (escrita estrita) em `crm.ts`; CRM
 * `features/sellers/SellerConfigPage.tsx` (validação do campo).
 *
 * Issues: #740 (regra de 12 meses), #748, #753 (cadastro do colaborador).
 */
export const CommissionWindowMonthsSchema = z
  .number()
  .int()
  .min(COMMISSION_WINDOW_MONTHS_MIN)
  .max(COMMISSION_WINDOW_MONTHS_MAX);

/**
 * Prazo da comissão de um cadastro: N meses (1–120) ou `null` = tempo
 * indeterminado (enquanto a assinatura existir).
 *
 * Origem: #780 — 2026-09-29 12:48 +0200 (prazo no cadastro de parceiro,
 * vendedor e gerente).
 *
 * Por quê: o owner quis a opção "indeterminado" nos três cadastros; `null` é o
 * mesmo sentido que `SplitBeneficiarySchema.window_months` já dava ao parceiro
 * (#750), então snapshot e cadastro falam a mesma língua.
 *
 * Usado por: `CrmUserSchema`/`CrmUpdateUserConfigInputSchema`/`CrmSellerRowSchema`
 * (`crm.ts`); `CrmPartnerSchema`/`CrmUpsertPartnerInput` (`crmPartner.ts`);
 * `resolveCommissionWindow` (este módulo); CRM `CommissionDurationField`.
 *
 * Dicas: leitura tolerante é responsabilidade de quem usa (`.catch` com o
 * default do cadastro: 12 no colaborador, `null` no parceiro) — este schema é
 * estrito.
 *
 * Issues: #780, #748, #740.
 */
export const CommissionWindowSchema = CommissionWindowMonthsSchema.nullable();
export type CommissionWindow = z.infer<typeof CommissionWindowSchema>;

/**
 * "No máximo 2 casas" com tolerância de ponto flutuante: `10.12 * 100` é
 * `1011.9999999999999` em binário, e a comparação exata recusava um percentual
 * digitado corretamente.
 *
 * Origem: #753 — 2026-09-27 12:23 +0200 (cadastro do parceiro; correção da A1).
 *
 * Contrato: `true` se `n` tem no máximo 2 casas decimais, com tolerância 1e-6
 * sobre o valor escalado. Puro.
 *
 * Usado por: `SplitPartnerPercentSchema` (este módulo); CRM
 * `features/partners/partnerForm.ts` (validação do formulário do parceiro,
 * a mesma regra que o servidor aplica).
 *
 * Falhas conhecidas:
 *  - 2026-09-27 12:23 +0200 (#753): a versão da A1 comparava `n * 100` com
 *    `Math.round(n * 100)` exatamente e recusava 10,12 e 0,07.
 *
 * Dicas: 1e-6 é folgado de propósito — o erro de representação de um valor com
 * 2 casas fica na casa de 1e-13; qualquer coisa perto de 1e-6 é uma 3ª casa
 * digitada.
 *
 * Histórico:
 *  - 2026-09-27 12:23 +0200 — (#753) criada com tolerância.
 */
export function hasAtMostTwoDecimals(n: number): boolean {
  // Bug corrigido em 2026-09-27 12:23 +0200 (#753): a comparação exata
  // `n * 100 === Math.round(n * 100)` recusava 10,12 e 0,07. Causa: erro de
  // representação binária. Não voltar para igualdade exata.
  const scaled = n * 100;
  return Math.abs(scaled - Math.round(scaled)) < 1e-6;
}

/**
 * Percentual do parceiro no cadastro: 0–50, até 2 casas.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Por quê: é o schema de ESCRITA (estrito); a leitura do cadastro é tolerante
 * (`CrmPartnerSchema.commissionPercent`, `.catch(null)`), para um valor
 * gravado fora da faixa não trancar o parceiro fora do CRM (#748, 12:34).
 *
 * Usado por: `CrmUpsertPartnerInput.commissionPercent` (`crmPartner.ts`, a
 * callable `crmUpsertPartner` do codebase `apps/crm-functions`).
 *
 * Issues: #748, #750, #753.
 */
export const SplitPartnerPercentSchema = z
  .number()
  .min(0)
  .max(SPLIT_PARTNER_PERCENT_MAX)
  .refine(hasAtMostTwoDecimals, 'No máximo 2 casas decimais.');

/**
 * `walletId` de uma carteira Asaas (UUID) — medido no spike A0 (#749): a API
 * devolve UUID em minúsculas (`00000000-0000-4000-8000-000000000000`,
 * anonimizado).
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Por quê: schema de ESCRITA — o formulário não grava carteira malformada. A
 * EXISTÊNCIA da carteira não é provada aqui: não há GET que resolva carteira
 * de conta externa; só uma cobrança-sonda com split prova (docs/billing/asaas.md,
 * "Carteira do split", medido em 2026-09-27 para a #753).
 *
 * Usado por: `asaasDestinationOrNull` (este módulo); `CrmUpsertPartnerInput`
 * (`crmPartner.ts`); `CrmUpdateUserConfigInputSchema` (`crm.ts`); CRM
 * `features/sellers/SellerConfigPage.tsx`.
 *
 * Dicas: o regex não fixa versão/variante do UUID — o Asaas não documenta a
 * versão e o spike só garantiu "minúsculo".
 *
 * Issues: #749 (spike A0), #750, #753.
 */
export const AsaasWalletIdSchema = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, 'walletId do Asaas inválido.');

/**
 * A carteira como o split pode usá-la: válida, ou `null` (⇒ `MANUAL`, extrato
 * a pagar).
 *
 * Origem: #748 — 2026-09-27 12:34 +0200 (auditoria da A1/A4).
 *
 * Por quê: os cadastros LEEM a carteira de forma tolerante para não trancar
 * ninguém fora do CRM; é aqui, no uso, que um valor inválido deixa de ser
 * destino de dinheiro. Normaliza (trim + minúsculas) antes de validar, porque
 * a carteira pode ter sido colada do painel do Asaas com espaço ou caixa alta.
 *
 * Contrato: `null`/`undefined`/inválida → `null`; válida → a forma normalizada.
 * Puro, nunca lança.
 *
 * Usado por: `internalDestination` e `partnerSplitDestination` (este módulo),
 * que alimentam `freezeSaleSplit` (`apps/functions/src/lib/billing/saleSplitSnapshot.ts`).
 *
 * Falhas conhecidas:
 *  - 2026-09-27 12:34 +0200 (#748): antes dela, a leitura ESTRITA da carteira
 *    em `CrmUserSchema`/`CrmPartnerSchema` fazia `getCrmUser`/`requireCrmPartner`
 *    devolverem `null` — um valor inválido trancava o colaborador ou o parceiro
 *    fora do CRM inteiro.
 *
 * Issues: #748 (auditoria A1/A4), #753.
 */
export function asaasDestinationOrNull(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const normalized = raw.trim().toLowerCase();
  return AsaasWalletIdSchema.safeParse(normalized).success ? normalized : null;
}

/**
 * Um beneficiário do split, JÁ CONGELADO: percentual, destino e janela são os
 * do instante da venda, e mudar o cadastro do colaborador ou do parceiro
 * depois não altera o que este cliente paga a ele (princípio 3).
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Usado por: `SalesSplitSchema.beneficiaries`; o tipo `SplitBeneficiary` em
 * `manualSplitBeneficiaries`/`splitAmountCents` (extrato a pagar, A4 #753).
 *
 * Dados — um item anonimizado (gerente do lead, sem carteira, venda interna):
 *   { "role": "MANAGER", "user_id": "uid-2", "percent": 5, "net_percent": 5,
 *     "destination": null, "transfer_mode": "MANUAL", "window_months": 12 }
 */
export const SplitBeneficiarySchema = z.object({
  /** Papel na venda — ver `SplitRoleSchema`. */
  role: SplitRoleSchema,
  /** `crm_users` uid. `null` só no PARTNER — ele é identificado por `partner_id`. */
  user_id: z.string().min(1).max(128).nullable(),
  /** O percentual configurado: PARTNER sobre o líquido; os demais sobre a parte do EtiquetaChef. */
  percent: z.number().min(0).max(100),
  /** Percentual efetivo sobre o líquido — o que o gateway recebe. Derivado e congelado. */
  net_percent: z.number().min(0).max(100),
  /** `walletId` Asaas ou `acct_…` do Connect. `null` ⇒ `MANUAL`. */
  destination: z.string().min(1).max(128).nullable(),
  /**
   * Derivado de `destination` + gateway (`transferModeFor`) e congelado junto:
   * quem lê o snapshot não precisa recalcular, e uma carteira que deixou de
   * ser válida depois da venda não muda o modo de uma cobrança futura.
   */
  transfer_mode: SplitTransferModeSchema,
  /** Meses de comissão a partir do 1º pagamento. `null` = enquanto a assinatura existir. */
  window_months: CommissionWindowMonthsSchema.nullable(),
});
export type SplitBeneficiary = z.infer<typeof SplitBeneficiarySchema>;

/**
 * O snapshot do split de UMA venda.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Por quê: as invariantes de negócio que o tipo sozinho não expressa estão no
 * `superRefine` (um parceiro só, `partner_id` coerente, sem colaborador
 * duplicado, soma ≤ 100 %) — `buildSalesSplit` faz `parse` com ele, então um
 * snapshot que as viole nunca chega ao Firestore.
 *
 * Contrato: `parse` lança `ZodError` na violação; `buildSalesSplit` propaga
 * (a venda não sai com split inválido).
 *
 * Usado por: `SalesSplitDocSchema.split`, `buildSalesSplit`, `asaasSplitRules`,
 * `manualSplitBeneficiaries` (este módulo); o tipo `SalesSplit` em
 * `apps/functions/src/lib/billing/saleSplitSnapshot.ts`.
 *
 * Dados: ver o exemplo no cabeçalho do módulo (`split`).
 *
 * Falhas conhecidas: 2026-09-27 12:34 +0200 (#748, auditoria A1/A4) — ver os
 * comentários de auditoria no `superRefine`.
 *
 * Histórico:
 *  - 2026-09-27 12:00 +0200 — (#750) criado.
 *  - 2026-09-27 12:34 +0200 — (#748) `partner_id` sem beneficiário, tolerância
 *    da soma, colaborador duplicado.
 */
export const SalesSplitSchema = z
  .object({
    /** Gateway da venda — ver `SplitGatewaySchema`. */
    gateway: SplitGatewaySchema,
    /**
     * `crm_partners` id do parceiro que trouxe o cliente, ou `null` em venda
     * da equipe interna. É atribuição, não pagamento: fica preenchido mesmo
     * quando o parceiro está a 0 % e não há beneficiário `PARTNER`.
     */
    partner_id: z.string().min(1).max(128).nullable(),
    /**
     * No máximo 4 porque é o maior elenco possível pelas regras de
     * `saleSplitInternals`: venda de parceiro = PARTNER + PARTNER_MANAGER (2);
     * venda interna = SELLER + MANAGER (2). O teto 4 deixa folga sem permitir
     * que uma lista inflada por bug vire regras de split no gateway.
     */
    beneficiaries: z.array(SplitBeneficiarySchema).max(4),
    /**
     * Início das janelas de comissão: o `period_start` do 1º pagamento. `null`
     * até ele — o webhook do 1º pagamento grava uma vez; troca de ciclo e
     * reativação não reiniciam (mesma regra da #740).
     *
     * No Asaas (#752) quem grava é `bindFirstAsaasPayment`
     * (`asaasHandlers.ts`), com o `dueDate` da 1ª cobrança convertido para
     * epoch ms (o Asaas manda só a data, `"2026-09-30"`, sem hora nem fuso).
     */
    window_start_at: z.number().int().nonnegative().nullable(),
    /**
     * Epoch ms em que o snapshot foi montado (o `now` do servidor, nunca o
     * relógio do cliente). Enquanto `window_start_at` é `null`, cada link novo
     * regrava o snapshot (`freezeSaleSplit`) e este carimbo anda junto.
     */
    frozen_at: z.number().int().nonnegative(),
    /**
     * Colaboradores que perderam TODA participação neste split — gravado pela
     * remoção de colaborador (`crmRemoveCollaborator`). #788 — 2026-09-30.
     * Opcional (ausente = `[]`): snapshots anteriores não têm o campo, e os
     * fixtures de teste montam `SalesSplit` sem ele.
     */
    revoked_user_ids: z.array(z.string().min(1)).optional(),
    /**
     * Gerentes que perderam só a parte de GERENTE (linhas MANAGER e
     * PARTNER_MANAGER) — gravado quando o gerente é rebaixado ou desativado
     * (`offboardManager`, #788, decisão do owner 2026-09-30: as comissões
     * futuras param e a parte dele não vai para ninguém). Como vendedor ele
     * continua. Opcional (ausente = `[]`).
     */
    revoked_manager_user_ids: z.array(z.string().min(1)).optional(),
  })
  .superRefine((split, ctx) => {
    // Dois PARTNER significariam dois repasses de parceiro sobre o mesmo
    // líquido — a soma ainda poderia caber em 100 %, então só a contagem pega.
    const partners = split.beneficiaries.filter((b) => b.role === SplitRole.PARTNER);
    if (partners.length > 1) {
      ctx.addIssue({ code: 'custom', message: 'No máximo um parceiro por venda.', path: ['beneficiaries'] });
    }
    // `partner_id` é a ATRIBUIÇÃO da venda, e vale mesmo com o parceiro a 0 %
    // (sem beneficiário PARTNER): o gerente do parceiro continua recebendo, e
    // extrato e relatórios precisam saber de quem é o cliente.
    // Auditoria 2026-09-27 12:34 +0200 (#748, A1/A4): com o parceiro a 0 % o
    // split saía sem `partner_id` — a venda ficava sem atribuição e o gerente
    // do parceiro sem parceiro. Causa: o refine exigia "beneficiário PARTNER ⇔
    // `partner_id`", e `buildSalesSplit` lia o id do beneficiário filtrado, que
    // some a 0 %. Não voltar ao "⇔": o extrato do gerente do parceiro perde a
    // origem.
    const needsPartner = split.beneficiaries.some(
      (b) => b.role === SplitRole.PARTNER || b.role === SplitRole.PARTNER_MANAGER,
    );
    if (needsPartner && split.partner_id === null) {
      ctx.addIssue({ code: 'custom', message: 'Beneficiário do parceiro exige partner_id.', path: ['partner_id'] });
    }
    // O mesmo uid duas vezes pagaria a mesma pessoa em dobro (ex.: o gerente
    // do lead que também é o dono do lead). `saleSplitInternals` já evita,
    // mas a invariante mora no schema para valer para qualquer montagem.
    // Auditoria 2026-09-27 12:34 +0200 (#748, A1/A4): o schema aceitava o
    // mesmo colaborador duas vezes. Não desfazer: seria comissão em dobro.
    const users = split.beneficiaries.flatMap((b) => (b.user_id === null ? [] : [b.user_id]));
    if (new Set(users).size !== users.length) {
      ctx.addIssue({ code: 'custom', message: 'O mesmo colaborador aparece duas vezes na venda.', path: ['beneficiaries'] });
    }
    // Soma > 100 % sobre o líquido é repasse maior que o dinheiro que entrou:
    // o gateway recusaria a assinatura inteira, e a venda falharia no cliente.
    const total = split.beneficiaries.reduce((acc, b) => acc + b.net_percent, 0);
    // Tolerância: `net_percent` tem 4 casas, e a soma em ponto flutuante de
    // valores que fecham exatamente 100 pode dar 100.00000000000001.
    // Auditoria 2026-09-27 12:34 +0200 (#748, A1/A4): a comparação era exata e
    // recusava um split que fecha 100 %. Não trocar `1e-9` por zero; mesma
    // classe do `hasAtMostTwoDecimals` (#753).
    if (total > 100 + 1e-9) {
      ctx.addIssue({ code: 'custom', message: 'A soma dos percentuais passa de 100 %.', path: ['beneficiaries'] });
    }
  });
export type SalesSplit = z.infer<typeof SalesSplitSchema>;

/**
 * Coleção raiz server-only com o split congelado de cada tenant (#748).
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Usado por: Functions — `lib/billing/saleSplitSnapshot.ts` (grava),
 * `billing/asaas/payOption.ts` (lê na escolha da `/pay`),
 * `billing/webhooks/asaas/asaasHandlers.ts` (abre a janela no 1º pagamento).
 *
 * Arquitetura: fora de `/tenants/{tenantId}/…` de propósito — nada ali é do
 * tenant cliente. Rules: leitura só super-admin, escrita só Admin SDK.
 */
export const SALES_SPLITS_COLLECTION = 'salesSplits';

/**
 * `/salesSplits/{tenantId}` — o split vigente do tenant. Um tenant tem uma
 * assinatura (#336 §2.0), logo um split; uma venda nova (troca de modelo,
 * recontratação) substitui o doc e deixa o anterior no audit.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Contrato: escrito por `freezeSaleSplit` (a cada link, enquanto
 * `window_start_at` é nulo) e por `bindFirstAsaasPayment` (só a janela);
 * lido por `payOption.ts` com `safeParse` — doc ausente ou ilegível faz a
 * escolha da `/pay` recusar (#752, B-M7).
 *
 * Usado por: `apps/functions/src/lib/billing/saleSplitSnapshot.ts`,
 * `apps/functions/src/billing/asaas/payOption.ts`.
 *
 * Dados: exemplo no cabeçalho do módulo.
 */
export const SalesSplitDocSchema = z.object({
  /** Redundante com o id do doc, gravado para que leituras em lote não dependam do path. */
  tenant_id: z.string().min(1).max(128),
  /** O snapshot vigente. */
  split: SalesSplitSchema,
  /** Epoch ms da última gravação do doc (a venda ou o `window_start_at` do 1º pagamento). */
  updated_at: z.number().int().nonnegative(),
  /**
   * Os uids de colaborador do snapshot (`beneficiaries[].user_id` não nulos),
   * desnormalizados para `array-contains`.
   *
   * Origem: #753 — 2026-09-27 21:10 +0200 (auditoria da A4).
   *
   * Por quê: a remoção de colaborador (`crmRemoveCollaborator` →
   * `revokeFromSales`) revoga pelos campos da `crm_sales` (vendedor e gerente),
   * e o gerente do PARCEIRO do split não está em nenhum deles:
   * removido e depois restaurado, ele voltava a gerar parcela MANUAL nas vendas
   * antigas. Com este campo a remoção acha as vendas dele pelo snapshot.
   *
   * Contrato: gravado por `freezeSaleSplit` junto com o `split`; ausente nos
   * docs anteriores (`.default([])`).
   */
  beneficiary_user_ids: z.array(z.string().min(1).max(128)).max(4).default([]),
  /**
   * #788 — o split mudou por revogação (`revoked_*`) e a assinatura do Asaas
   * ainda leva as regras antigas. Gravado `true` pelo CRM ao revogar; o passe
   * 12 do `dailyBillingTick` (`runSplitRevocationPass`) manda o `PUT` e zera.
   * Default `false`.
   */
  split_resync_pending: z.boolean().default(false),
});
export type SalesSplitDoc = z.infer<typeof SalesSplitDocSchema>;

/**
 * Os uids de colaborador de um snapshot — o valor de
 * `SalesSplitDoc.beneficiary_user_ids`.
 *
 * Origem: #753 — 2026-09-27 21:10 +0200 (auditoria da A4).
 */
export function splitBeneficiaryUserIds(split: SalesSplit): string[] {
  return split.beneficiaries.flatMap((b) => (b.user_id === null ? [] : [b.user_id]));
}

/**
 * Arredonda para as 4 casas que o Asaas aceita (`SPLIT_PERCENT_DECIMALS`).
 *
 * Origem: #750 — 2026-09-27 12:00 +0200.
 *
 * Por quê: arredondamento comum (não truncamento) — a diferença é de no
 * máximo 0,00005 ponto percentual, que em cents some no `Math.round` do valor.
 *
 * Usado por: `internalNetPercent`, `buildSalesSplit` (este módulo).
 */
function roundPercent(n: number): number {
  const f = 10 ** SPLIT_PERCENT_DECIMALS;
  return Math.round(n * f) / f;
}

/**
 * Percentual interno convertido para a base do gateway (o líquido inteiro).
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Por quê: o cadastro do colaborador fala em "% da parte do EtiquetaChef"; o
 * gateway só conhece "% do líquido". Ex. do owner: vendedor 10 % com parceiro
 * a 50 % → `10 × (100 − 50) / 100` = 5 % do líquido.
 *
 * Contrato: puro; resultado arredondado a 4 casas.
 *
 * Usado por: `buildSalesSplit` (este módulo); `salesSplit.test.ts`.
 */
export function internalNetPercent(percent: number, partnerPercent: number): number {
  return roundPercent((percent * (100 - partnerPercent)) / 100);
}

/**
 * Modo de repasse de um beneficiário.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200.
 *
 * Por quê: sem destino ⇒ `MANUAL` (extrato a pagar), em qualquer gateway — a
 * regra do owner de nunca bloquear a venda por falta de carteira (#748). Com
 * destino, o modo é o repasse nativo do gateway da venda.
 *
 * Usado por: `buildSalesSplit` (este módulo).
 */
function transferModeFor(gateway: SplitGateway, destination: string | null): SplitTransferMode {
  if (destination === null) return 'MANUAL';
  return gateway === 'asaas' ? 'ASAAS_SPLIT' : 'STRIPE_CONNECT';
}

/**
 * Entrada de `buildSalesSplit`, já resolvida pelo servidor (cadastros lidos,
 * carteiras normalizadas por `asaasDestinationOrNull`/`partnerSplitDestination`).
 *
 * Origem: #750 — 2026-09-27 12:00 +0200.
 *
 * Usado por: `buildSalesSplit`, `saleSplitInternals` (tipo de retorno) e
 * `freezeSaleSplit` (`apps/functions/src/lib/billing/saleSplitSnapshot.ts`).
 */
export type BuildSalesSplitArgs = {
  /** Gateway da venda (`resolveSaleGateway`). */
  gateway: SplitGateway;
  /**
   * O parceiro DIRECT que trouxe o cliente (`percent` = `commissionPercent` do
   * cadastro, `destination` = carteira válida ou `null`), ou `null` em venda
   * interna.
   */
  partner: {
    id: string;
    percent: number;
    destination: string | null;
    /** Prazo do cadastro do parceiro (#780); `null` = indeterminado. */
    window_months: number | null;
  } | null;
  /** Os internos, em percentual SOBRE A PARTE DO ETIQUETACHEF — ver `saleSplitInternals`. */
  internal: {
    role: Exclude<SplitRole, typeof SplitRole.PARTNER>;
    user_id: string;
    percent: number;
    destination: string | null;
    /** `null` = indeterminado (#780). */
    window_months: number | null;
  }[];
  /** Epoch ms do servidor; vira `frozen_at`. */
  now: number;
};

/**
 * Monta o snapshot congelado da venda.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Por quê: beneficiário com percentual 0 não entra (parceiro a 0 % = venda sem
 * split de parceiro, mas a base interna continua sendo o líquido inteiro).
 * Lança se o resultado violar o schema — é erro de programação ou de cadastro,
 * e a venda não pode sair assim.
 *
 * Contrato: puro; devolve `SalesSplit` com `window_start_at: null` e
 * `frozen_at: args.now`. Lança `ZodError` (via `SalesSplitSchema.parse`).
 *
 * Usado por: `freezeSaleSplit` (`apps/functions/src/lib/billing/saleSplitSnapshot.ts`,
 * chamado pelo link da venda em `createSaleCheckoutLink`); `salesSplit.test.ts`.
 *
 * Arquitetura: princípio 3 (comissão congelada) — o snapshot é a fonte da
 * verdade do que cada cobrança paga; o cadastro atual não é relido depois do
 * 1º pagamento.
 *
 * Falhas conhecidas: 2026-09-27 12:34 +0200 (#748) — o `partner_id` sumia
 * com o parceiro a 0 % (comentário local abaixo).
 *
 * Dicas: o exemplo do owner (100 → 50 / 5 / 2,5) está fixado em
 * `salesSplit.test.ts`; rode `npm test -w @etiquetachef/types`.
 *
 * Histórico:
 *  - 2026-09-27 12:00 +0200 — (#750) criado.
 *  - 2026-09-27 12:34 +0200 — (#748) `partner_id` do `args.partner`, não do filtrado.
 *  - 2026-09-29 12:48 +0200 — (#780) prazo do parceiro vem do cadastro (`partner.window_months`).
 */
export function buildSalesSplit(args: BuildSalesSplitArgs): SalesSplit {
  // `partner` (beneficiário) some a 0 %, mas `partnerPercent` usa o valor do
  // cadastro de qualquer forma — a 0 % a conversão dos internos é identidade,
  // então as duas leituras concordam.
  const partner = args.partner && args.partner.percent > 0 ? args.partner : null;
  const partnerPercent = args.partner?.percent ?? 0;
  const beneficiaries: SplitBeneficiary[] = [];
  if (partner) {
    beneficiaries.push({
      role: SplitRole.PARTNER,
      user_id: null,
      percent: partner.percent,
      // O percentual do parceiro JÁ é sobre o líquido: só arredonda.
      net_percent: roundPercent(partner.percent),
      destination: partner.destination,
      transfer_mode: transferModeFor(args.gateway, partner.destination),
      // Prazo do cadastro do parceiro (#780, 2026-09-29 12:48 +0200). Até
      // então o parceiro não tinha janela e recebia sempre (`null`), que
      // continua sendo o default do cadastro.
      window_months: partner.window_months,
    });
  }
  for (const i of args.internal) {
    // Colaborador sem percentual configurado (`?? 0` em `saleSplitInternals`)
    // não vira regra de split de valor zero no gateway.
    if (i.percent <= 0) continue;
    beneficiaries.push({
      role: i.role,
      user_id: i.user_id,
      percent: i.percent,
      net_percent: internalNetPercent(i.percent, partnerPercent),
      destination: i.destination,
      transfer_mode: transferModeFor(args.gateway, i.destination),
      window_months: i.window_months,
    });
  }
  return SalesSplitSchema.parse({
    gateway: args.gateway,
    // Do `args.partner` (não do `partner` filtrado): a atribuição sobrevive ao 0 %.
    // Auditoria 2026-09-27 12:34 +0200 (#748, A1/A4): com o parceiro a 0 % a
    // venda perdia o `partner_id`. Causa: lido do beneficiário filtrado. Não
    // trocar por `partner?.id`.
    partner_id: args.partner?.id ?? null,
    beneficiaries,
    // Nasce sem janela aberta; quem a abre é o webhook do 1º pagamento
    // (`bindFirstAsaasPayment`, #752), uma única vez.
    window_start_at: null,
    frozen_at: args.now,
  });
}

/**
 * Soma `months` meses de calendário (UTC), com o dia travado no último dia do
 * mês de destino — 31/jan + 1 mês = 28 ou 29/fev, nunca 3/mar.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200.
 *
 * Contrato: puro; epoch ms → epoch ms, preservando hora/minuto/segundo UTC.
 *
 * Usado por: `isWithinCommissionWindow` (este módulo); `salesSplit.test.ts`.
 *
 * Dicas: UTC, não BRT — o início da janela é um instante, e a comparação é
 * entre instantes; um fuso aqui só deslocaria os dois lados.
 */
export function addCalendarMonthsUtc(epochMs: number, months: number): number {
  const d = new Date(epochMs);
  const day = d.getUTCDate();
  // Monta o alvo no DIA 1 para o `Date.UTC` não transbordar (31/jan + 1 mês
  // com dia 31 viraria 3/mar) e só depois põe o dia, travado no último do mês.
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1,
    d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds()));
  // Dia 0 do mês seguinte = último dia do mês alvo.
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return target.getTime();
}

/**
 * O beneficiário recebe numa cobrança cujo período começa em `periodStartAt`?
 *
 * Origem: #750 — 2026-09-27 12:00 +0200.
 *
 * Contrato: sem janela (`null` = indeterminado, #780) ou antes do 1º pagamento: `true`. Depois:
 * só se o período começa antes do fim da janela (fim exclusivo). Puro.
 *
 * Usado por: `asaasSplitRules`, `manualSplitBeneficiaries` (este módulo);
 * `salesSplit.test.ts`.
 *
 * Issues: #740 (janela contada do 1º pagamento), #748.
 */
export function isWithinCommissionWindow(
  b: Pick<SplitBeneficiary, 'window_months'>,
  windowStartAt: number | null,
  periodStartAt: number,
): boolean {
  if (b.window_months === null || windowStartAt === null) return true;
  // Fim exclusivo: o período que começa exatamente no aniversário da janela
  // já é o primeiro sem comissão.
  return periodStartAt < addCalendarMonthsUtc(windowStartAt, b.window_months);
}

/**
 * As regras de split do Asaas para uma cobrança cujo período começa em
 * `periodStartAt`: só beneficiários `ASAAS_SPLIT` dentro da janela.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas).
 *
 * Contrato: lista vazia ⇒ a assinatura deve ir sem split (`split: []` no
 * `PUT`). Puro.
 *
 * Usado por: `apps/functions/src/billing/asaas/payOption.ts` (split do
 * checkout de cartão, da assinatura de boleto e da autorização Pix, com
 * `periodStartAt = now`); `salesSplit.test.ts`.
 *
 * Dados — o que vai ao Asaas, no vocabulário da ASSINATURA (medido no spike A0,
 * #749): `[{ "walletId": "00000000-0000-4000-8000-000000000000",
 * "percentualValue": 20 }]`. O checkout hospedado usa outro nome
 * (`splits[].percentageValue`) — a tradução é do cliente do Asaas
 * (`providers/asaas.ts`), não daqui.
 *
 * Dicas: o `PUT` de split não retroage (spike A0): quem chama tem de aplicar a
 * lista ANTES de o Asaas gerar a cobrança do período — e o Asaas gera a
 * cobrança 40 dias antes do vencimento (docs/billing/asaas.md, A2).
 *
 * Issues: #749 (spike), #750, #752 (uso na `/pay`).
 */
/**
 * O beneficiário perdeu o direito neste split? (#788)
 *
 * Origem: #788 — 2026-09-30.
 *
 * Contrato: puro. Revogado quando o uid está em `revoked_user_ids` (remoção)
 * ou, só nas linhas de gerente (MANAGER/PARTNER_MANAGER), em
 * `revoked_manager_user_ids` (gerente rebaixado ou desativado). Linha sem
 * `user_id` (o parceiro) nunca é revogada por aqui.
 *
 * Usado por: `asaasSplitRules` (regras mandadas ao Asaas) e
 * `planSplitCommissionLines` (`apps/crm-functions/src/lib/splitCommissions.ts`).
 */
export function isSplitBeneficiaryRevoked(
  split: Pick<SalesSplit, 'revoked_user_ids' | 'revoked_manager_user_ids'>,
  b: Pick<SplitBeneficiary, 'role' | 'user_id'>,
): boolean {
  if (b.user_id === null) return false;
  if ((split.revoked_user_ids ?? []).includes(b.user_id)) return true;
  const isManagerLine = b.role === SplitRole.MANAGER || b.role === SplitRole.PARTNER_MANAGER;
  return isManagerLine && (split.revoked_manager_user_ids ?? []).includes(b.user_id);
}

export function asaasSplitRules(
  split: SalesSplit,
  periodStartAt: number,
): { walletId: string; percentualValue: number }[] {
  // Split congelado para o Stripe nunca vira regra de carteira Asaas.
  if (split.gateway !== 'asaas') return [];
  // `destination !== null` é redundante com `ASAAS_SPLIT` (o modo só nasce com
  // destino), mas estreita o tipo para `walletId: string` sem asserção.
  // #788: beneficiário revogado sai das regras — a carteira dele deixa de
  // receber no próximo PUT da assinatura (a parte fica com o EtiquetaChef).
  return split.beneficiaries.flatMap((b) =>
    b.transfer_mode === 'ASAAS_SPLIT' &&
    b.destination !== null &&
    !isSplitBeneficiaryRevoked(split, b) &&
    isWithinCommissionWindow(b, split.window_start_at, periodStartAt)
      ? [{ walletId: b.destination, percentualValue: b.net_percent }]
      : [],
  );
}

/**
 * Quem, numa cobrança, recebe pelo extrato a pagar (sem destino no gateway),
 * dentro da janela.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200.
 *
 * Por quê: é o complemento de `asaasSplitRules` — todo beneficiário na janela
 * sai por um dos dois caminhos, nunca pelos dois. Base de cálculo do valor: o
 * líquido da cobrança × `net_percent` (`splitAmountCents`).
 *
 * Usado por: só `salesSplit.test.ts` hoje. O consumidor é o extrato a pagar
 * (A4, #753) — a A3 só congela o snapshot.
 *
 * Issues: #753 (extrato a pagar).
 */
export function manualSplitBeneficiaries(split: SalesSplit, periodStartAt: number): SplitBeneficiary[] {
  return split.beneficiaries.filter(
    (b) => b.transfer_mode === 'MANUAL' && isWithinCommissionWindow(b, split.window_start_at, periodStartAt),
  );
}

/**
 * Valor em cents de um beneficiário sobre o líquido de uma cobrança.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200.
 *
 * Contrato: cents → cents, arredondado ao cent mais próximo. Puro.
 *
 * Usado por: só `salesSplit.test.ts` hoje; consumidor previsto: extrato a
 * pagar (A4, #753).
 *
 * Dicas: o Asaas truncou no spike A0 (#749): líquido 193,44 × 20 % = 38,688 →
 * 38,68 no Asaas; aqui daria 38,69. A diferença de 1 cent só afeta o extrato
 * `MANUAL`, onde o valor é nosso, não do gateway — não use esta função para
 * conferir o repasse `ASAAS_SPLIT`.
 */
export function splitAmountCents(netCents: number, b: Pick<SplitBeneficiary, 'net_percent'>): number {
  return Math.round((netCents * b.net_percent) / 100);
}

/**
 * O que o split precisa saber de um colaborador (`crm_users`).
 *
 * Origem: #752 — 2026-09-27 14:28 +0200 (A3: contrato da venda pelo Asaas).
 *
 * Por quê: estrutural — não importa `CrmUser` para este módulo não depender do
 * schema do CRM, e os campos são opcionais/nulos porque cadastros antigos não
 * os têm.
 *
 * Usado por: `saleSplitInternals`, `windowOf` (este módulo); `readCollaborator`
 * em `apps/functions/src/lib/billing/saleSplitSnapshot.ts` (monta a partir do
 * `CrmUserSchema`; colaborador ilegível vira `null` e sai do split).
 */
export type SplitCollaborator = {
  /** Uid do `crm_users` — vira `user_id` do beneficiário. */
  uid: string;
  /** Papel no CRM (`SELLER`, `MANAGER`, `PARTNER`, …); string para aceitar valor desconhecido sem lançar. */
  role: string;
  /** % sobre a parte do EtiquetaChef quando fecha a venda. Ausente ⇒ 0 (não entra no split). */
  sellerCommissionPercent?: number | null | undefined;
  /** % sobre a parte do EtiquetaChef quando é o gerente do lead. Ausente ⇒ 0. */
  managerCommissionPercent?: number | null | undefined;
  /**
   * Janela própria; `null` = indeterminado (#780); ausente ou ilegível ⇒
   * `COMMISSION_WINDOW_MONTHS_DEFAULT`.
   */
  commissionWindowMonths?: number | null | undefined;
  /** Carteira crua do cadastro; só vira destino depois de `asaasDestinationOrNull`. */
  asaasWalletId?: string | null | undefined;
  /**
   * Status no CRM (#788). Gerente (do lead ou do parceiro) só entra no split
   * se for MANAGER (ou ADMIN) e ACTIVE; ausente = não conferido (compatível com quem
   * ainda não passa o campo).
   */
  status?: string | undefined;
};

/**
 * O colaborador pode entrar no split como GERENTE? (#788)
 *
 * Origem: #788 — 2026-09-30. Antes, gerente rebaixado ou desativado seguia
 * entrando nos links novos dos parceiros dele e nas vendas dos leads que
 * ainda o apontavam.
 *
 * Usado por: `saleSplitInternals` (este arquivo).
 */
function canReceiveAsManager(m: SplitCollaborator): boolean {
  // ADMIN também administra quadros e tem percentual de gerente — em produção
  // há leads com ADMIN como gerente (conferido em 2026-09-30); cortá-lo seria
  // regressão. Rebaixado a SELLER/AUDITOR ou desativado sai.
  const managesByRole = m.role === CrmRole.MANAGER || m.role === CrmRole.ADMIN;
  return managesByRole && (m.status === undefined || m.status === 'ACTIVE');
}

/**
 * Destino de um colaborador INTERNO no split.
 *
 * Origem: #752 — 2026-09-27 14:28 +0200.
 *
 * Por quê: colaborador interno não tem conta Connect — no Stripe a comissão
 * dele é sempre do extrato a pagar. No Asaas, a carteira válida ou `null`.
 *
 * Usado por: `saleSplitInternals` (este módulo).
 */
function internalDestination(gateway: SplitGateway, wallet: string | null | undefined): string | null {
  // Colaborador interno não tem conta Connect: no Stripe a comissão dele é
  // sempre do extrato a pagar.
  return gateway === 'asaas' ? asaasDestinationOrNull(wallet) : null;
}

/**
 * Janela do colaborador, tolerante.
 *
 * Origem: #752 — 2026-09-27 14:28 +0200.
 *
 * Por quê: valor fora de 1–120 ou ausente cai no default em vez de lançar — um
 * cadastro mal preenchido não pode barrar a venda (mesma política de
 * `asaasDestinationOrNull`, #748).
 *
 * Usado por: `saleSplitInternals` (este módulo).
 *
 * Histórico:
 *  - 2026-09-29 12:48 +0200 — (#780) `null` (indeterminado) é preservado;
 *    antes virava 12.
 */
function windowOf(user: SplitCollaborator | null): number | null {
  // `null` é escolha do cadastro ("tempo indeterminado", #780), não valor
  // ilegível: não pode cair no default de 12.
  if (user?.commissionWindowMonths === null) return null;
  const parsed = CommissionWindowMonthsSchema.safeParse(user?.commissionWindowMonths);
  return parsed.success ? parsed.data : COMMISSION_WINDOW_MONTHS_DEFAULT;
}

/**
 * Prazo do cadastro do PARCEIRO, tolerante: ausente ou ilegível ⇒ `null`
 * (indeterminado, o comportamento de antes da #780).
 *
 * Origem: #780 — 2026-09-29 12:48 +0200.
 *
 * Contrato: puro; recebe o valor cru de `crm_partners/{id}.commissionWindowMonths`.
 *
 * Usado por: `saleSplitInternals`, `resolveCommissionWindow` (este módulo);
 * `freezeSaleSplit` (`apps/functions/src/lib/billing/saleSplitSnapshot.ts`).
 */
export function partnerCommissionWindowOf(raw: unknown): number | null {
  const parsed = CommissionWindowMonthsSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Prazo do cadastro de um COLABORADOR interno, tolerante: `null` ⇒
 * indeterminado; ausente ou ilegível ⇒ `COMMISSION_WINDOW_MONTHS_DEFAULT`.
 *
 * Origem: #780 — 2026-09-29 12:48 +0200.
 *
 * Contrato: puro; recebe o valor cru de `crm_users/{uid}.commissionWindowMonths`.
 *
 * Usado por: `resolveCommissionWindow` (este módulo); `CrmUserSchema`
 * segue a mesma regra por `.default(12).catch(12)` (`crm.ts`).
 */
export function collaboratorCommissionWindowOf(raw: unknown): number | null {
  return windowOf({ uid: '', role: '', commissionWindowMonths: raw as number | null | undefined });
}

/**
 * Prazo VIGENTE de um beneficiário do split, lido do cadastro atual.
 *
 * Origem: #780 — 2026-09-29 12:48 +0200 (owner: mudar o prazo no cadastro
 * afeta também as vendas já fechadas).
 *
 * Por quê: o percentual continua congelado na venda, mas o prazo não — o
 * owner decidiu que ele segue o cadastro. O snapshot (`window_months`) vira
 * só o fallback de quando o cadastro não pôde ser lido (apagado, ilegível).
 *
 * Contrato: puro.
 *  - `PARTNER` e `PARTNER_MANAGER` → prazo de `crm_partners/{partner_id}`
 *    (decisão 6 da #780: o prazo do parceiro vale para o gerente dele);
 *  - `SELLER` e `MANAGER` → prazo de `crm_users/{user_id}`.
 *  `partnerDoc`/`userDoc` `undefined` = não lido ou inexistente ⇒ `frozen`.
 *
 * Usado por: `readBeneficiaryWindows` (`apps/crm-functions/src/lib/commissionWindow.ts`,
 * geração das parcelas); `readAsaasSplitRules`
 * (`apps/functions/src/billing/asaas/recurringValue.ts`, regras do gateway);
 * `salesSplit.test.ts`.
 *
 * Arquitetura: a regra mora aqui para os dois codebases (`functions` e
 * `crm-functions`) decidirem igual; cada um só lê os documentos.
 *
 * Issues: #780, #766 (o gateway só aprende o prazo novo no próximo `PUT`).
 */
export function resolveCommissionWindow(args: {
  role: SplitRole;
  frozen: number | null;
  userDoc?: { commissionWindowMonths?: unknown } | undefined;
  partnerDoc?: { commissionWindowMonths?: unknown } | undefined;
}): number | null {
  if (args.role === SplitRole.PARTNER || args.role === SplitRole.PARTNER_MANAGER) {
    return args.partnerDoc === undefined
      ? args.frozen
      : partnerCommissionWindowOf(args.partnerDoc.commissionWindowMonths);
  }
  return args.userDoc === undefined
    ? args.frozen
    : collaboratorCommissionWindowOf(args.userDoc.commissionWindowMonths);
}

/**
 * Os beneficiários INTERNOS de uma venda, pela regra do owner (2026-09-27,
 * cabeçalho deste módulo).
 *
 * Origem: #752 — 2026-09-27 14:28 +0200 (A3: split com o snapshot completo).
 *
 * - **Venda de parceiro** → só o gerente do parceiro (`crm_partners.managerUserId`),
 *   com o `managerCommissionPercent` do CADASTRO DO PARCEIRO. **Parceiro sem
 *   gerente (#786) → nenhum interno**: sem beneficiário `PARTNER_MANAGER` não
 *   há regra de split no Asaas, transfer no Stripe nem comissão MANUAL a
 *   pagar depois (`splitCommissions.ts` itera só os beneficiários).
 * - **Fechada por MANAGER** → só ele, com o percentual de vendedor dele.
 * - **Fechada por outro papel** → quem fechou (percentual de vendedor) + o
 *   gerente do lead (percentual de gerente), quando existe e não é o mesmo.
 *
 * A participação não entra: ela foi removida do sistema inteiro na #758
 * (2026-09-28), e `resolveFrozenBeneficiaries` do CRM segue a mesma regra.
 *
 * Contrato: puro; devolve a lista `internal` de `BuildSalesSplitArgs` (0 a 2
 * itens), com percentual sobre a parte do EtiquetaChef.
 *
 * Usado por: `freezeSaleSplit` (`apps/functions/src/lib/billing/saleSplitSnapshot.ts`);
 * `salesSplit.test.ts`.
 *
 * Falhas conhecidas: o snapshot completo manda os internos no split ANTES da
 * dedupe da comissão CRM paga por fora (`paidVia: SPLIT`) — risco de comissão
 * em dobro registrado na #752 (W-L6), resolvido na A4 (#753:
 * `isCommissionPaidByGateway`, linhas `ASAAS_SPLIT` informativas no CRM) —
 * pré-requisito da A7 (#756) cumprido.
 *
 * Issues: #752 (A3), #753 (A4: dedupe), #756 (A7: virada), #758 (participação),
 * #780 (prazo).
 *
 * Histórico:
 *  - 2026-09-29 12:48 +0200 — (#780) gerente do parceiro usa o prazo do
 *    cadastro do parceiro; `null` (indeterminado) preservado nos internos.
 *  - 2026-09-30 16:22 +0200 — (#786) gerente do parceiro opcional: `null`
 *    devolve `[]`, sem comissão de gerente em nenhum gateway.
 *  - 2026-10-13 19:09 +0200 — (#965) `manualOnly`: no parceiro RESELLER o
 *    gerente recebe só pelo extrato a pagar.
 */
export function saleSplitInternals(args: {
  gateway: SplitGateway;
  partner: {
    /** #786: `null` = parceiro sem gerente — nenhum interno recebe. */
    managerUserId: string | null;
    managerCommissionPercent: number;
    manager: SplitCollaborator | null;
    /** Prazo cru do cadastro do parceiro (#780) — vale para o gerente dele. */
    commissionWindowMonths?: unknown;
    /**
     * #965 — parceiro `RESELLER`: a comissão do gerente vai SEMPRE para o
     * extrato a pagar (destino `null` → `MANUAL`), nunca por split/transfer.
     * O owner decidiu que esse modelo não trabalha com split: a cobrança sai
     * sem regra nenhuma e a NFS-e pelo líquido cheio.
     */
    manualOnly?: boolean;
  } | null;
  /** Dono do lead (quem fecha a venda). */
  seller: SplitCollaborator | null;
  /** `lead.managerUserId`, resolvido. */
  leadManager: SplitCollaborator | null;
}): BuildSalesSplitArgs['internal'] {
  const { gateway } = args;
  if (args.partner) {
    // Venda de parceiro: o vendedor e o gerente do lead NÃO entram — o
    // interno pago é o gerente do parceiro, pelo percentual do cadastro do
    // parceiro. `manager` só fornece a carteira; se não pôde ser lido, o
    // gerente continua no split, em `MANUAL`.
    // #786 (owner 2026-09-30): parceiro sem gerente não gera comissão de
    // gerente. O corte é AQUI, na origem do split, e não no pagamento: é
    // deste beneficiário que saem os três caminhos (regra de split Asaas,
    // transfer Stripe e comissão MANUAL "a pagar depois"). Não trocar por um
    // beneficiário a 0 % nem com `user_id: null` — `buildSalesSplit` e o
    // `saleBeneficiaryWindows` do CRM atribuiriam a linha ao parceiro.
    const managerUserId = args.partner.managerUserId;
    if (managerUserId === null) return [];
    const m = args.partner.manager;
    // #788: gerente lido e que já não é MANAGER ativo (rebaixado/desativado)
    // não entra. Doc ilegível (`m === null`) continua como antes: entra em
    // MANUAL, e a checagem de pagamento decide.
    if (m !== null && !canReceiveAsManager(m)) return [];
    return [
      {
        role: SplitRole.PARTNER_MANAGER,
        user_id: managerUserId,
        percent: args.partner.managerCommissionPercent,
        // #965 (2026-10-13 19:09 +0200): RESELLER não leva split — sem destino,
        // a linha nasce MANUAL e entra no extrato a pagar.
        destination: args.partner.manualOnly ? null : internalDestination(gateway, m?.asaasWalletId),
        // #780 (2026-09-29 12:48 +0200, decisão 6 do owner): o prazo do
        // cadastro do PARCEIRO vale para o gerente dele. Até então vinha do
        // `crm_users` do gerente (`windowOf(m)`).
        window_months: partnerCommissionWindowOf(args.partner.commissionWindowMonths),
      },
    ];
  }
  const seller = args.seller;
  // Sem dono legível, ou dono com papel PARTNER (usuário de parceiro fechando
  // lead sem `partnerId` na venda): nenhum interno recebe. Um usuário de
  // parceiro nunca é pago como vendedor interno.
  // O vendedor aqui é um usuário do CRM: o papel é `CrmRole`, não `SplitRole` (#761).
  if (!seller || seller.role === CrmRole.PARTNER) return [];
  const out: BuildSalesSplitArgs['internal'] = [
    {
      role: SplitRole.SELLER,
      user_id: seller.uid,
      percent: seller.sellerCommissionPercent ?? 0,
      destination: internalDestination(gateway, seller.asaasWalletId),
      window_months: windowOf(seller),
    },
  ];
  const manager = args.leadManager;
  // MANAGER que fecha sozinho recebe só como SELLER (regra do owner); e o
  // gerente do lead que é o próprio vendedor não entra de novo (o
  // `superRefine` recusaria o uid duplicado).
  // `seller.role` é o papel do CRM (`CrmRole`); o `role` gravado abaixo é o do
  // split (`SplitRole`) — mesmos textos, conceitos diferentes (#761).
  // #788: e só se o gerente do lead ainda for MANAGER ativo.
  if (seller.role !== CrmRole.MANAGER && manager && manager.uid !== seller.uid && canReceiveAsManager(manager)) {
    out.push({
      role: SplitRole.MANAGER,
      user_id: manager.uid,
      percent: manager.managerCommissionPercent ?? 0,
      destination: internalDestination(gateway, manager.asaasWalletId),
      window_months: windowOf(manager),
    });
  }
  return out;
}

/**
 * Destino do PARCEIRO no split.
 *
 * Origem: #752 — 2026-09-27 14:28 +0200 (A3).
 *
 * Contrato: Asaas → a carteira do cadastro, normalizada, ou `null`; Stripe →
 * sempre `null` (a conta Connect entra na A8, #757; até lá a comissão vai para
 * o extrato a pagar). Puro.
 *
 * Usado por: `freezeSaleSplit` (`apps/functions/src/lib/billing/saleSplitSnapshot.ts`);
 * `salesSplit.test.ts`.
 *
 * Issues: #752, #757 (Connect).
 */
export function partnerSplitDestination(
  gateway: SplitGateway,
  partner: { asaasWalletId?: string | null | undefined },
): string | null {
  return gateway === 'asaas' ? asaasDestinationOrNull(partner.asaasWalletId) : null;
}
